import React, { useEffect, useMemo, useRef, useState } from "react";

import { LazyItem, Sentinel } from "./LazyScrollList.styled";
import { LazyScrollListProps } from "./LazyScrollList.types";

const DEFAULT_INITIAL_COUNT = 5;
const DEFAULT_BATCH_SIZE = 5;
const DEFAULT_ROOT_MARGIN = "200px";

function LazyScrollList<T>({
    items,
    renderItem,
    getItemKey,
    initialCount = DEFAULT_INITIAL_COUNT,
    batchSize = DEFAULT_BATCH_SIZE,
    emptyState,
    rootMargin = DEFAULT_ROOT_MARGIN,
}: LazyScrollListProps<T>) {
    const [visibleCount, setVisibleCount] = useState(initialCount);
    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {

        setVisibleCount(initialCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length, initialCount]);

    const hasMore = visibleCount < items.length;

    useEffect(() => {
        if (!hasMore) return undefined;
        const node = sentinelRef.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisibleCount((count) =>
                        Math.min(items.length, count + batchSize),
                    );
                }
            },
            { rootMargin },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, items.length, batchSize, rootMargin]);

    const visibleItems = useMemo(
        () => items.slice(0, visibleCount),
        [items, visibleCount],
    );

    if (!items.length) {
        return <>{emptyState ?? null}</>;
    }

    return (
        <>
            {visibleItems.map((item, index) => (
                <LazyItem key={getItemKey(item, index)}>
                    {renderItem(item, index)}
                </LazyItem>
            ))}
            {hasMore && <Sentinel ref={sentinelRef} aria-hidden="true" />}
        </>
    );
}

export default LazyScrollList;
